import {
  SITE_NAME,
  SITE_URL,
  TWITTER_HANDLE,
  DEFAULT_OG_IMAGE,
  absoluteUrl,
} from './site'
import type { SeoProfileName } from './seo-profiles'

export type RouteSeo = {
  /** under ~60 chars, unique per route */
  title: string
  /** 50 to 160 chars, unique per route */
  description: string
  /** absolute or site-relative share image; defaults to DEFAULT_OG_IMAGE */
  image?: string
  /** og:type, defaults to "website" */
  type?: 'website' | 'article'
  /** extra JSON-LD blocks emitted into the head for this route */
  jsonLd?: Record<string, unknown>[]
  /** target keyphrase for the content-quality report (public pages) */
  keyphrase?: string
  /**
   * Site-relative path of the machine-readable markdown variant of this page
   * (blog posts; emitted by scripts/gen-blog-markdown.mjs). Advertised in the
   * head as <link rel="alternate" type="text/markdown"> so AI engines can
   * fetch and cite the raw markdown directly.
   */
  markdownPath?: string
  /**
   * Absolute URL of the canonical version when it lives off-site (syndicated
   * or co-published content). Overrides the default self-canonical.
   */
  canonicalUrl?: string
  /**
   * RSS feed this page belongs to (blog and changelog pages). Advertised in
   * the head as <link rel="alternate" type="application/rss+xml"> so feed
   * readers and content-monitoring tools can autodiscover it from any page
   * of the section. The feeds themselves are emitted by scripts/gen-feeds.mjs.
   */
  feed?: { href: string; title: string }
  /**
   * Override the content-quality profile for this route. Defaults to the
   * page type inferred from the path (see `profileForPath`): blog/changelog
   * are judged as articles, everything else as a landing page.
   */
  contentType?: SeoProfileName
  /** Emit <meta name="robots" content="noindex"> (error and utility pages). */
  noindex?: boolean
  /**
   * ISO dates for article pages, emitted as article:published_time and
   * article:modified_time Open Graph tags so engines read machine-readable
   * dates from the head, not just the visible byline.
   */
  publishedTime?: string
  modifiedTime?: string
  /** Author name for article pages, emitted as article:author. */
  articleAuthor?: string
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function meta(attrs: Record<string, string>): string {
  const a = Object.entries(attrs)
    .map(([k, v]) => `${k}="${esc(v)}"`)
    .join(' ')
  return `<meta ${a} />`
}

/**
 * Build the per-route <head> as an HTML string, injected at prerender time:
 * title, description, canonical, Open Graph, Twitter, and any JSON-LD.
 * `canonicalPath` is always the route's canonical path (so aliases point home).
 */
export function renderHead(seo: RouteSeo, canonicalPath: string): string {
  // canonicalUrl overrides for syndicated content whose original lives
  // off-site; everything else (og:url, JSON-LD) keeps our own URL.
  const url = absoluteUrl(canonicalPath === '/' ? '/' : canonicalPath.replace(/\/+$/, ''))
  const canonicalHref = seo.canonicalUrl ?? url
  const image = absoluteUrl(seo.image ?? DEFAULT_OG_IMAGE)
  const type = seo.type ?? 'website'

  const parts: string[] = [
    `<title>${esc(seo.title)}</title>`,
    meta({ name: 'description', content: seo.description }),
    `<link rel="canonical" href="${esc(canonicalHref)}" />`,
    ...(seo.markdownPath
      ? [
          `<link rel="alternate" type="text/markdown" href="${esc(absoluteUrl(seo.markdownPath))}" />`,
        ]
      : []),
    ...(seo.feed
      ? [
          `<link rel="alternate" type="application/rss+xml" title="${esc(seo.feed.title)}" href="${esc(absoluteUrl(seo.feed.href))}" />`,
        ]
      : []),
    ...(seo.noindex ? [meta({ name: 'robots', content: 'noindex' })] : []),
    meta({ property: 'og:type', content: type }),
    ...(type === 'article' && seo.publishedTime
      ? [meta({ property: 'article:published_time', content: seo.publishedTime })]
      : []),
    ...(type === 'article' && (seo.modifiedTime ?? seo.publishedTime)
      ? [
          meta({
            property: 'article:modified_time',
            content: seo.modifiedTime ?? seo.publishedTime ?? '',
          }),
        ]
      : []),
    ...(type === 'article' && seo.articleAuthor
      ? [meta({ property: 'article:author', content: seo.articleAuthor })]
      : []),
    meta({ property: 'og:site_name', content: SITE_NAME }),
    meta({ property: 'og:title', content: seo.title }),
    meta({ property: 'og:description', content: seo.description }),
    meta({ property: 'og:url', content: url }),
    meta({ property: 'og:image', content: image }),
    meta({ name: 'twitter:card', content: 'summary_large_image' }),
    meta({ name: 'twitter:site', content: TWITTER_HANDLE }),
    meta({ name: 'twitter:title', content: seo.title }),
    meta({ name: 'twitter:description', content: seo.description }),
    meta({ name: 'twitter:image', content: image }),
  ]

  for (const block of seo.jsonLd ?? []) {
    parts.push(`<script type="application/ld+json">${JSON.stringify(block)}</script>`)
  }

  return parts.join('\n    ')
}

/** Organization + WebSite JSON-LD for the homepage. */
export function siteJsonLd(): Record<string, unknown>[] {
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: absoluteUrl('/logo.png'),
      sameAs: [
        'https://github.com/langwatch/langwatch',
        'https://www.linkedin.com/company/langwatch',
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE_NAME,
      url: SITE_URL,
    },
  ]
}

/** sitemap.xml body for the given canonical paths. */
export function renderSitemap(paths: string[], lastmod?: Record<string, string>): string {
  const urls = paths
    .map((p) => {
      const loc = `<loc>${esc(absoluteUrl(p === '/' ? '/' : p))}</loc>`
      // Only content pages carry <lastmod>, sourced from their frontmatter
      // date. A build timestamp on every URL would tell crawlers everything
      // changed on every deploy, which makes lastmod useless for recrawl
      // scheduling; better to omit it where we have no truthful value.
      const mod = lastmod?.[p]
      return `  <url>${loc}${mod ? `<lastmod>${esc(mod.slice(0, 10))}</lastmod>` : ''}</url>`
    })
    .join('\n')
  return (
    `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}\n</urlset>\n`
  )
}

/** robots.txt pointing at every sitemap, including the docs one. */
export function renderRobots(): string {
  return (
    `User-agent: *\nAllow: /\n\n` +
    `Sitemap: ${absoluteUrl('/sitemap.xml')}\n` +
    // The docs live under /docs (proxied); their sitemap is served there and
    // is not part of this build, so it must be listed explicitly.
    `Sitemap: ${absoluteUrl('/docs/sitemap.xml')}\n`
  )
}
